import { TailwindConfig } from '@ankorstore/design-system';
import { GlobalMetaTagName } from '@modules/core/services/initial-state';
import { getInjectedContent } from '@modules/core/services/injected-content';
import type { FeatureFlagService } from '@monolith/legacy/types/feature-flag-service';

const theme = TailwindConfig.theme;
const getFlags = (): Record<string, string> => getInjectedContent<Record<string, string>>(GlobalMetaTagName.Features) ?? {};

export const getExperiments = function (): Record<string, string> {
  return getInjectedContent<Record<string, string>>(GlobalMetaTagName.Experiments) ?? {};
};

export const getBrowserId = function (): string | null {
  return getInjectedContent<string>(GlobalMetaTagName.BrowserId) ?? null;
};

export const getVariant = function (featureName: FeatureFlag | string): string {
  const flags = getFlags();
  if (Object.prototype.hasOwnProperty.call(flags, featureName)) {
    return flags[featureName];
  }
  return 'off';
};

export const isEnabled = function (featureName: string): boolean {
  const variant = getVariant(featureName);
  return 'off' !== variant && 'ineligible' !== variant;
};

const screens = Object.keys(theme.screens).reduce((acc, key) => {
  acc[key] = parseInt(theme.screens[key], 10);
  return acc;
}, {});

export const appTheme = {
  screens,
  fonts: theme.fontFamily,
  colors: theme.colors,
};

export const configureVue = function (app) {
  app.config.globalProperties.$experiments = getExperiments;
  app.config.globalProperties.$isEnabled = isEnabled;
  app.config.globalProperties.$variant = getVariant;
  app.config.globalProperties.$theme = appTheme;
};

export enum FeatureFlag {
  Ankorstart = 'retailer_signup_ankorstart',
  CatalogueExport = 'catalog_export',
  CheckoutSilentValidation = 'checkout_silent_validation',
  FilterBar = 'filter_bar_v0',
  FiltersModal = 'filters_modal_v0',
  InternationalShippingMessages = 'international_shipping_messages',
  LeadTime = 'leadtime',
  LegacyMapsWidget = 'legacy_maps_widget',
  LocalisedSearch = 'localised_search',
  MandatoryEori = 'mandatory_eori',
  PickupCancellation = 'pickup_cancellation',
  productsRecommendations = 'RET-1337',
  ProductVariants = 'product_variants',
  Ravelin = 'ravelin',
  RecommendationsRedirect = 'project_path_to_moria',
  RegisterGeodataVerification = 'register_geodata_verification',
  RegistrationFunnelMockedData = 'RCSGT-611',
  RegistrationFunnelRevamp = 'RCSGT-453',
  RegistrationRename = 'registration_rename',
  SignUpAutoOpen = 'signup_auto_open',
  SignUpPersonalize = 'sign_up_personalize',
  SignUpPromocode = 'sign_up_promocode',
  SuggestionsService = 'suggestions_service',
  SumupOffer = 'sumup_offer',
  WelcomeDiscountOffer = 'welcome_discount_offer',
  FulfillmentReplenishmentViewNewWarehouseWarning = 'fulfillment_replenishment_view_new_warehouse_warning',
  Auth2FA = 'auth.multi_factor_authentication',
  EnableNewBusinessModel = 'enable_new_business_model',
  CartBrandRecommendationsCarousel = 'cart_brand_recommendations_carousel',
  CartBrandRecommendations = 'cart_brand_recommendations',
  HolidayMode = 'holiday_mode',
  TSClientFulfillmentLots = 'FF-1539',
  MNWebinar = 'mn_webinar',
  MergeShippingFlowsIteration5 = 'iteration_5_merge_flow',
  AvailabilityInVariants = 'bc-ordering.OXP-2263',
  CampaignThrottling = 'campaign-throttling',
  SplitOrders = 'OXP-2268',
  Leads = 'leads',
  AccountingExportV1 = 'accounting_export_v1',
  AccountingExportV2 = 'accounting_export_v2',
  AccountingExportV2CompleteFormat = 'accounting_export_v2_complete_format',
  AccountingExportOnDemandPolling = 'accounting_export_on_demand_polling',
  NafoShippingDeliveryNewBadge = 'nafo.shippingDeliveryNewBadge',
  DirectOrderDocumentImport = 'direct_order.factory_for_nafo',
  DirectOrderFactoring = 'direct_order.factoring',
  SendSamplesTool = 'send_samples_tool',
  CampaignEditorPluginCTA = 'campaign_editor_plugin_cta',
  CampaignEditorPluginLink = 'campaign_editor_plugin_link',
  CampaignEditorPluginImage = 'campaign_editor_plugin_image',
  CampaignEditorPluginProductCard = 'campaign_editor_plugin_product_card',
  AddContactsAutoInvite = 'add_contacts_auto_invite',
  MultiTierDiscounts = 'multi_tier_discounts',
  CampaignEditorPluginAiContent = 'campaign_editor_plugin_ai_content',
  EditOrderStockColumn = 'bc-ordering.INT-484',
  EditOrderFreeItems = 'bc-ordering.INT-441',
  EditOrderIteration4 = 'bc-ordering.INT-616',
  AksDays = 'comms.ankorstore_days',
  CrmMVP = 'crm_mvp',
  OffersAndDiscountsPage = 'offers_and_discounts_page',
  CreditCardForServiceAsBrand = 'enable_credit_card_for_service_as_brand',
  EnableNewBillingSetup = 'enable_new_billing_setup',
  EnableMarketplaceAsService = 'enable_marketplace_as_service',
  CancelOrderpaySubscription = 'cancel_orderpay_subscription',
  AriaValidationBoxInSettings = 'PUR-2287',
  OffersAndEventsPage = 'offers_and_events_page',
  EnableServiceNewReleaseDateBadge = 'enable_service_new_release_date_badge',
  EnableNbmBanner = 'INT-680',
  StoreDiscount = 'store_discount',
  ShowTotalOrderPage = 'show_total_order_page',
  BrandVideoLibrary = 'brand_video_library',
  BrandsWithAdsEnabled = 'MNY_115',
  ShowToplineBanner = 'RLO-1509',
  CreditLimitPillNavbar = 'credit_limit_pill_navbar',
  SearchEventCollection = 'DAT-2836',
  ChunkLoadErrorHandler = 'chunk_load_error_handler',
  RetailerOnboardingBusinessId = 'retailer_onboarding_business_id',
  SimplifiedRegistration = 'RET-4253',
  GermanBusinessIdentifierModal = 'german_business_identifier_modal',
  UnconnectedBrandPage = 'RET-4161',
  BrandPromotePage = 'brand-shop.promote-page',
  BrandPageCollectionsBanner = 'DAT-2947',
  SearchRecentBrands = 'DAT-2946',
  FundedDiscounts = 'funded_discounts',
  SmartReorderEnabled = 'smart_reorder_enabled',
  FreshProductFields = 'fresh_product_fields',
  WelcomeOfferBanner = 'welcome_offer_banner',
  KlaviyoSync = 'klaviyo_sync',
  QrCampaigns = 'brand_qr_campaigns',
  CampaignsScheduling = 'campaigns.scheduling',
  CampaignsSchedulingCancellation = 'campaigns.scheduling.cancellation',
  DisableBrandFinanceEmailBanner = 'disable_brand_finance_email_banner',
  RetailerPreferences = 'INT-1337',
  PaypalInstallmentsGroup = 'paypal_installments_group',
  DisableWaxIntegration = 'disable_RET-4459_wax_integration',
  CollectionAtDockDisabled = 'disable_SER-919_collection_at_dock',
  RailyDisabled = 'disable_RET-4429_raily',
  // @variant
  SharedAddToCart = 'shared_addtocart',
  DisableBrandsOfTheWeek = 'disable_DAT-3195_brands_of_the_week',
  DisableAriaKycRefusal = 'disable_PUR-3208_aria_kyc_refusal',
  DisablePriceReport = 'disable_PRICE-10_price_report',
  DisableBrandTabs = 'disable_PRICE-39_brand_tabs',
  /** @variant Product-tile A/B experiment: control | simplified (read via getVariant). */
  TileSimplification = 'PRICE-2',
  /** @variant Quick-view-drawer redesign A/B experiment: control | redesign (read via getVariant). */
  QuickViewRedesign = 'PRICE-20',
  DisableFloatingCart = 'disable_RCN-196_floating_cart',
  DisableDiscountFilterSortHighlight = 'disable_PRICE-8_discount_filter_sort_highlight',
  DisableBrandPageSortBy = 'disable_SEL-85_brand_page_sort_by',
  DisableWhatsNewPage = 'disable_YANN-228_whats_new_page',
  DisableBrandRegistrationLandingPage = 'disable_SEL-55_brand_registration_landing_page',
  DisableOldCartUi = 'disable_RCN-373_old_cart_ui',
}

export const featureFlagService: FeatureFlagService = {
  getExperiments,
  getVariant,
  isEnabled,
};
